import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import ReactApexChart from "react-apexcharts"

import { httpOk, GET } from "helpers/api"
import { formatDate, formatMoney } from "helpers/helpers"
import AnalyticsDatePicker from "../AnalyticsDatePicker"
import AnalyticsFilterBar, { FilterField, FilterSegment, StandardFields } from "../parts/AnalyticsFilterBar"
import useAnalyticsFilters from "../parts/useAnalyticsFilters"
import useAnalyticsOptions from "../parts/useAnalyticsOptions"
import { buildAnalyticsUrl, localDateString, parseLocalDate } from "../parts/analyticsDates"

const cardStyle = {
	border: "1px solid var(--dark-border, #edf0f4)",
	borderRadius: "12px",
	boxShadow: "0 4px 14px rgba(15, 23, 42, 0.04)",
}

const mutedStyle = {
	color: "var(--dark-text-muted, #8791a1)",
	fontSize: "12px",
}

const compareOptions = [
	{ id: "week", label: "Неделю назад" },
	{ id: "day", label: "Вчера" },
	{ id: "year", label: "Год назад" },
]

const chartModes = [
	{ id: "hourly", label: "По часам" },
	{ id: "dayparts", label: "Части дня" },
]

function dayString(offset) {
	const date = new Date()
	date.setHours(0, 0, 0, 0)
	date.setDate(date.getDate() + offset)
	return localDateString(date)
}

function percentClass(value) {
	return Number(value) >= 0 ? "text-success" : "text-danger"
}

function formatPercent(value) {
	return `${Number(value || 0).toFixed(1).replace(".0", "")}%`
}

// Рост от нулевой базы приходит null — это «новое», а не +100%
function formatGrowth(value) {
	if (value === null || value === undefined) return "новое"
	return `${Number(value) >= 0 ? "+" : ""}${formatPercent(value)}`
}

function formatMetric(value) {
	const number = Number(value || 0)
	return formatMoney(number, 0)
}

function formatChartDate(value) {
	return value ? formatDate(`${value}T00:00:00`, "dd.MM.yyyy") : ""
}

function metricActual(metric) {
	return metric && typeof metric === "object" ? metric.actual : metric
}

function metricGrowth(metric) {
	return metric && typeof metric === "object" ? metric.growth_percent : null
}

function KpiCard({ title, value, growth, icon, compareLabel, showGrowth = true }) {
	const isNew = growth === null || growth === undefined
	return (
		<div className="card mb-3 h-100" style={cardStyle}>
			<div className="card-body py-3 px-4">
				<div className="d-flex align-items-start gap-2 h-100">
					<i className={`uil ${icon} text-muted`} style={{ fontSize: "16px", lineHeight: "20px" }}></i>
					<div className="d-flex flex-column justify-content-between">
						<div style={mutedStyle}>{title}</div>
						<h4 className="mb-1">{value}</h4>
						{showGrowth ? (
							<div className="d-flex align-items-center gap-2" style={{ fontSize: "12px" }}>
								<span
									className={isNew ? "text-muted" : percentClass(growth)}
									style={{
										background: isNew ? "var(--dark-surface-2, #f1f4f8)" : Number(growth) >= 0 ? "var(--dark-tint-success, #e7f8ef)" : "var(--dark-tint-danger, #fdecec)",
										borderRadius: "14px",
										padding: "2px 8px",
									}}
								>
									{formatGrowth(growth)}
								</span>
								{compareLabel && <span className="text-muted">{compareLabel}</span>}
							</div>
						) : (
							<div style={{ height: "22px" }}></div>
						)}
					</div>
				</div>
			</div>
		</div>
	)
}

function SectionCard({ title, subtitle, children, className = "", actions = null, titleIcon = null }) {
	return (
		<div className={`card mb-3 ${className}`} style={cardStyle}>
			<div className="card-body">
				<div className="d-flex justify-content-between align-items-start gap-3 mb-3 flex-wrap">
					<div>
						<h5 className="mb-1 d-flex align-items-center gap-2">
							{titleIcon}
							{title}
						</h5>
						{subtitle && <div style={mutedStyle}>{subtitle}</div>}
					</div>
					{actions}
				</div>
				{children}
			</div>
		</div>
	)
}

function ProgressLine({ percent }) {
	const value = Math.max(0, Math.min(Number(percent || 0), 100))
	const barClass = Number(percent || 0) >= 100 ? "bg-success" : "bg-warning"
	return (
		<div className="progress" style={{ height: "8px", borderRadius: "20px" }}>
			<div className={`progress-bar ${barClass}`} style={{ width: `${value}%`, borderRadius: "20px" }}></div>
		</div>
	)
}

function shortMetric(shortValue, value) {
	// Полное число из value; готовая строка с бэка — только если числа нет
	if (value !== null && value !== undefined) return formatMetric(value)
	return shortValue || formatMetric(0)
}

function ProductChangeBlock({ title, items = [], type = "growth" }) {
	if (!items.length) return null

	const isGrowth = type === "growth"
	return (
		<div
			className="mt-3 p-2 rounded-3"
			style={{
				background: isGrowth ? "var(--dark-surface-2, #f0fbf7)" : "var(--dark-surface-2, #fff5f5)",
				border: `1px solid ${isGrowth ? "var(--dark-tint-border-success, #d7f3e7)" : "var(--dark-tint-border-danger, #ffe0e0)"}`,
			}}
		>
			<div className={`mb-2 fw-semibold ${isGrowth ? "text-success" : "text-danger"}`} style={{ fontSize: "12px" }}>
				{title}
			</div>
			{items.slice(0, 5).map(product => (
				<div className="d-flex justify-content-between gap-2 small mb-1" key={product.product_id}>
					<div className="text-truncate">{product.product_name}</div>
					<div className="text-end flex-shrink-0">
						<span className="text-muted">
							{formatMoney(product.current_quantity || 0, 2)} vs {formatMoney(product.previous_quantity || 0, 2)} {product.unit_name}
						</span>
						<span className={`ms-2 ${isGrowth ? "text-success" : "text-danger"}`}>
							{shortMetric(product.growth_amount_short, product.growth_amount)}
						</span>
						<span className={`ms-2 ${isGrowth ? "text-success" : "text-danger"}`}>
							{formatGrowth(product.growth_percent)}
						</span>
					</div>
				</div>
			))}
		</div>
	)
}

function PlanMetricRow({ label, fact, plan }) {
	return (
		<div className="d-flex justify-content-between align-items-center small mb-2">
			<span className="text-muted">{label}</span>
			<b>{fact} / {plan}</b>
		</div>
	)
}

function GrowthValue({ value }) {
	if (value === null || value === undefined) return <span className="text-muted">новое</span>
	const isPositive = Number(value) >= 0
	return (
		<span className={isPositive ? "text-success" : "text-danger"}>
			<span style={{ fontSize: "13px" }}>{isPositive ? "↗" : "↘"}</span> {formatPercent(value)}
		</span>
	)
}

/**
 * Выполнение плана в строке таблицы: процент и тонкая полоска под ним. Заливка всей ячейки
 * здесь не годится — в последней колонке она выглядит отдельной плашкой, а при плане,
 * выполненном в десятки раз, огромный процент в цветном блоке читается как ошибка.
 * Полоска заполняется до 100 %, перевыполнение видно по цифре.
 */
function PlanProgress({ percent }) {
	if (percent === null) return <span className="text-muted">—</span>

	const isDone = percent >= 100
	const color = isDone ? "var(--dark-fg-success, #1f9d57)" : "var(--dark-fg-warning, #e0a020)"
	// Перевыполнение в разы — без дробной части, иначе «6178.8%» только шумит
	const label = percent >= 1000 ? `${formatMoney(Math.round(percent), 0)}%` : formatPercent(percent)

	return (
		<div className="d-inline-block text-end" style={{ minWidth: "96px" }}>
			<div className="fw-semibold" style={{ color }}>{label}</div>
			<div className="mt-1 rounded-pill" style={{ height: "4px", background: "var(--dark-surface-3, #edf0f4)", overflow: "hidden" }}>
				<div className="rounded-pill" style={{ width: `${Math.max(0, Math.min(100, percent))}%`, height: "100%", background: color }} />
			</div>
		</div>
	)
}

function PlanCard({ item }) {
	const noPlan = item.status === "no_plan"
	const isDone = Number(item.execution_percent || 0) >= 100

	return (
		<div className="col-xl-4 col-md-6">
			<div className="border rounded-3 p-3 h-100">
				<div className="d-flex justify-content-between align-items-start mb-2">
					<div>
						<h6 className="mb-1">{item.name}</h6>
						<div style={mutedStyle}>{item.status_text}</div>
					</div>
					<b className={noPlan ? "text-muted" : isDone ? "text-success" : "text-warning"}>
						{noPlan ? "—" : formatPercent(item.execution_percent)}
					</b>
				</div>
				<div className="mb-3">
					<PlanMetricRow
						label="Выручка"
						fact={shortMetric(item.revenue?.fact_short, item.revenue?.fact)}
						plan={shortMetric(item.revenue?.plan_short, item.revenue?.plan)}
					/>
					<PlanMetricRow
						label="Чеки"
						fact={formatMoney(item.cheques?.fact || 0)}
						plan={formatMoney(item.cheques?.plan || 0)}
					/>
					<PlanMetricRow
						label="Ср. чек"
						fact={shortMetric(item.average_cheque?.fact_short, item.average_cheque?.fact)}
						plan={shortMetric(item.average_cheque?.plan_short, item.average_cheque?.plan)}
					/>
				</div>
				<ProgressLine percent={item.execution_percent} />
				<ProductChangeBlock title="Что просело" items={item.top_dropped_products || []} type="dropped" />
				<ProductChangeBlock title="Что выросло" items={item.top_growth_products || []} type="growth" />
			</div>
		</div>
	)
}

const orderTypeColors = ["#4aa3df", "#36c690", "#f0b23a", "#d84b5b", "#8f5bd8", "#2f80c9"]

function OrderTypeCard({ item, index }) {
	const color = orderTypeColors[index % orderTypeColors.length]
	const rows = [
		{ label: "Чеки", value: formatMoney(item.cheques || 0), growth: item.cheques_growth_percent },
		{ label: "Выручка", value: formatMetric(item.revenue), growth: item.revenue_growth_percent },
		{ label: "Гости", value: formatMoney(item.guests || 0), growth: item.guests_growth_percent },
	]

	return (
		<div className="col-xl-4 col-md-6">
			<div className="border rounded-3 p-3 h-100">
				<div className="d-flex justify-content-between mb-2">
					<h6 className="mb-0 d-flex align-items-center gap-2">
						<span style={{ width: "10px", height: "10px", borderRadius: "50%", background: color, display: "inline-block" }}></span>
						{item.name}
					</h6>
					<span style={mutedStyle}>{formatPercent(item.share_revenue_percent)}</span>
				</div>
				<div className="mt-3 small">
					{rows.map(row => (
						<div className="d-flex justify-content-between align-items-center mb-2" key={row.label}>
							<span className="text-muted">{row.label}</span>
							<div className="text-end">
								<b>{row.value}</b>
								<span className="ms-2"><GrowthValue value={row.growth} /></span>
							</div>
						</div>
					))}
					<div className="progress mt-3" style={{ height: "8px", borderRadius: "20px" }}>
						<div
							className="progress-bar"
							style={{
								width: `${Math.min(Math.max(Number(item.share_revenue_percent || 0), 0), 100)}%`,
								borderRadius: "20px",
								background: color,
							}}
						></div>
					</div>
				</div>
			</div>
		</div>
	)
}

function ProductList({ items }) {
	return (
		<div>
			{items?.length > 0 ? items.map((item, index) => (
				<div className="d-flex align-items-center justify-content-between gap-3 mb-3" key={item.product_id}>
					<div className="d-flex align-items-center gap-3 overflow-hidden">
						<span className="text-muted">{item.rank || index + 1}</span>
						<span className="text-truncate">{item.product_name}</span>
					</div>
					<div className="d-flex align-items-center gap-3 text-end flex-shrink-0">
						<span>{formatMetric(item.revenue)}</span>
						<span style={{ fontSize: "12px" }}><GrowthValue value={item.growth_percent} /></span>
					</div>
				</div>
			)) : (
				<div className="text-muted">Нет данных</div>
			)}
		</div>
	)
}

function planSourceLabel(source) {
	if (source === "manual") return "ручной план"
	if (source === "auto") return "авто-план по истории"
	if (source === "mixed") return "ручной + авто-план"
	return "план не задан"
}

function Index() {
	const { t } = useTranslation()
	const [data, setData] = useState(null)
	const [chartMode, setChartMode] = useState("hourly")

	const filters = useAnalyticsFilters({
		pos_id: -1,
		date: dayString(-1),
		compare: "week",
		order_type_ids: [],
		source_ids: [],
	}, searchWithFilter)
	const { draft, applied, patch } = filters
	const options = useAnalyticsOptions(["sources"], draft.pos_id)

	async function searchWithFilter(filter) {
		const response = await GET(buildAnalyticsUrl("/analytics/daily", filter), {}, { loader: true })
		if (httpOk(response)) setData(response.data)
	}

	const today = dayString(0)
	const yesterday = dayString(-1)
	const dayPreset = draft.date === today ? "today" : draft.date === yesterday ? "yesterday" : null

	const hourlyChart = useMemo(() => ({
		series: [
			{ name: formatChartDate(data?.date), data: data?.hourly?.map(item => Number(item.current_revenue || 0)) || [] },
			{ name: formatChartDate(data?.compare_date), data: data?.hourly?.map(item => Number(item.compare_revenue || 0)) || [] },
		],
		options: {
			chart: { type: "bar", height: 320, toolbar: { show: false } },
			colors: ["#d74b4b", "#cbd5e1"],
			dataLabels: { enabled: false },
			plotOptions: { bar: { borderRadius: 4, columnWidth: "48%" } },
			xaxis: { categories: data?.hourly?.map(item => `${String(item.hour).padStart(2, "0")}:00`) || [] },
			yaxis: { labels: { formatter: value => formatMetric(value) } },
			tooltip: { y: { formatter: value => formatMoney(value, 0) } },
			legend: { position: "top", horizontalAlign: "right" },
		},
	}), [data])

	const daypartsChart = useMemo(() => ({
		series: data?.dayparts?.series?.map(item => ({
			name: /^\d{4}-\d{2}-\d{2}$/.test(item.name) ? formatChartDate(item.name) : item.name,
			data: item.data?.map(value => Number(value || 0)) || [],
		})) || [],
		options: {
			chart: { type: "bar", height: 320, toolbar: { show: false } },
			colors: ["#d74b4b", "#cbd5e1"],
			dataLabels: { enabled: false },
			plotOptions: { bar: { borderRadius: 4, columnWidth: "50%" } },
			xaxis: { categories: data?.dayparts?.categories || [] },
			yaxis: { labels: { formatter: value => formatMetric(value) } },
			tooltip: { y: { formatter: value => formatMoney(value, 0) } },
			legend: { position: "top", horizontalAlign: "right" },
		},
	}), [data])

	const summary = data?.summary || {}
	const overall = data?.plan_execution?.overall || {}
	const pageDate = data?.date ? formatChartDate(data.date) : ""
	const compareDate = data?.compare_date ? formatChartDate(data.compare_date) : ""
	const compareLabel = [data?.compare_label, compareDate ? `(${compareDate})` : ""].filter(Boolean).join(" ")
	const subtitle = pageDate
		? `${pageDate}${data?.weekday ? ` — ${data.weekday}` : ""}${data?.is_today ? " (день не закрыт)" : ""} · сравнение: ${compareDate}${data?.compare_weekday ? ` — ${data.compare_weekday}` : ""}`
		: ""

	return (
		<>
			<AnalyticsFilterBar
				title="Дэйли"
				icon="uil-calendar-alt"
				subtitle={subtitle}
				filters={filters}
				presets={false}
			>
				<StandardFields filters={filters} options={options} />
				<FilterField label="День" wide>
					<div className="d-flex align-items-center gap-2 flex-wrap">
						<FilterSegment
							options={[{ id: "yesterday", label: "Вчера" }, { id: "today", label: "Сегодня" }]}
							value={dayPreset}
							onChange={(id) => patch({ date: id === "today" ? today : yesterday })}
						/>
						<div style={{ width: "140px" }}>
							<AnalyticsDatePicker
								selected={parseLocalDate(draft.date)}
								onChange={(date) => date && patch({ date: localDateString(date) })}
							/>
						</div>
					</div>
				</FilterField>
				<FilterField label="Сравнить с" wide>
					<FilterSegment options={compareOptions} value={draft.compare} onChange={(id) => patch({ compare: id })} />
				</FilterField>
			</AnalyticsFilterBar>

			<div className="row align-items-stretch mb-3">
				<div className="col-xl-3 col-md-6 mb-3">
					<KpiCard
						title={t("revenue")}
						value={formatMetric(metricActual(summary.revenue))}
						growth={metricGrowth(summary.revenue)}
						compareLabel={compareLabel}
						icon="uil-money-bill"
					/>
				</div>
				<div className="col-xl-3 col-md-6 mb-3">
					<KpiCard
						title={t("cheques")}
						value={formatMoney(metricActual(summary.cheques))}
						growth={metricGrowth(summary.cheques)}
						compareLabel={compareLabel}
						icon="uil-receipt"
					/>
				</div>
				<div className="col-xl-3 col-md-6 mb-3">
					<KpiCard
						title={t("average_cheque")}
						value={formatMetric(metricActual(summary.average_cheque))}
						growth={metricGrowth(summary.average_cheque)}
						compareLabel={compareLabel}
						icon="uil-chart-line"
					/>
				</div>
				<div className="col-xl-3 col-md-6 mb-3">
					<KpiCard
						title={t("plan_execution")}
						value={overall.plan > 0 ? formatPercent(summary.plan_execution_percent) : "—"}
						showGrowth={false}
						icon="uil-focus-target"
					/>
				</div>
			</div>
			{Number(metricActual(summary.returns) || 0) > 0 && (
				<div className="mb-3" style={mutedStyle}>
					Возвраты за день: {formatMoney(metricActual(summary.returns), 0)} — вычтены из выручки и среднего чека.
				</div>
			)}

			<SectionCard title={t("plan_execution")} subtitle={`${planSourceLabel(overall.plan_source)} · ${compareLabel}`}>
				<div
					className="d-flex align-items-center justify-content-between gap-3 mb-3 p-3 rounded-3"
					style={{ background: "var(--dark-surface-2, #f6f7f9)", border: "1px solid var(--dark-border, #e8edf3)" }}
				>
					<div className="flex-fill">
						<div style={mutedStyle}>{t("overall_execution")}</div>
						<h3 className={Number(overall.execution_percent || 0) >= 100 ? "text-success mb-0" : "text-warning mb-0"}>
							{overall.plan > 0 ? formatPercent(overall.execution_percent) : "—"}
						</h3>
					</div>
					<div className="flex-fill text-center">
						<div style={mutedStyle}>{t("fact")}</div>
						<b>{shortMetric(overall.fact_short, overall.fact)}</b>
					</div>
					<div className="flex-fill text-end">
						<div style={mutedStyle}>{t("plan")}</div>
						<b>{overall.plan > 0 ? shortMetric(overall.plan_short, overall.plan) : "—"}</b>
					</div>
				</div>
				<div className="row g-3 mt-1">
					{data?.plan_execution?.by_order_type?.map(item => (
						<PlanCard item={item} key={item.code} />
					))}
				</div>
			</SectionCard>

			<SectionCard title={t("branches_breakdown")} subtitle="Все филиалы с продажами в выбранный день или в день сравнения">
				<div className="table-responsive">
					<table className="table align-middle mb-0" style={{ fontSize: "15px", borderCollapse: "separate", borderSpacing: 0 }}>
						<thead>
							<tr style={{ border: "none" }}>
								{[
									{ label: t("branch"), className: "" },
									{ label: t("revenue"), className: "text-end" },
									{ label: "Δ%", className: "text-end" },
									{ label: t("cheques"), className: "text-end" },
									{ label: "Δ%", className: "text-end" },
									{ label: t("average_cheque"), className: "text-end" },
									{ label: t("plan"), className: "text-end" },
								].map((column, index) => (
									<th key={index} className={`py-3 text-muted fw-semibold ${column.className}`} style={{ border: "none", boxShadow: "none", background: "var(--dark-surface, #fff)", fontSize: "13px" }}>
										{column.label}
									</th>
								))}
							</tr>
						</thead>
						<tbody>
							{data?.branches?.map((item, index) => {
								const borderBottom = index === data.branches.length - 1 ? "none" : "1px solid var(--dark-border, #eef2f6)"
								const hasPlan = item.plan_execution_percent !== null && item.plan_execution_percent !== undefined
								return (
									<tr key={item.pos_id}>
										<td className="py-3 fw-semibold" style={{ borderBottom }}>{item.pos_name}</td>
										<td className="py-3 text-end fw-medium" style={{ borderBottom }}>{formatMetric(item.revenue)}</td>
										<td className="py-3 text-end fw-medium" style={{ borderBottom }}><GrowthValue value={item.revenue_growth_percent} /></td>
										<td className="py-3 text-end fw-medium" style={{ borderBottom }}>{formatMoney(item.cheques)}</td>
										<td className="py-3 text-end fw-medium" style={{ borderBottom }}><GrowthValue value={item.cheques_growth_percent} /></td>
										<td className="py-3 text-end fw-medium" style={{ borderBottom }}>{formatMoney(item.average_cheque)}</td>
										<td className="py-3 text-end" style={{ borderBottom }} title={planSourceLabel(item.plan_source)}>
											<PlanProgress percent={hasPlan ? Number(item.plan_execution_percent) : null} />
										</td>
									</tr>
								)
							})}
							{!data?.branches?.length && (
								<tr>
									<td className="text-muted text-center" colSpan="7">{t("no_data")}</td>
								</tr>
							)}
						</tbody>
					</table>
				</div>
			</SectionCard>

			<SectionCard
				title={chartMode === "hourly" ? t("hourly_analysis") : t("dayparts_analysis")}
				subtitle="Выручка по часу закрытия чека, возвраты — по часу возврата. Ось начинается с открытия бизнес-дня."
				actions={<FilterSegment options={chartModes} value={chartMode} onChange={setChartMode} />}
			>
				<ReactApexChart
					options={chartMode === "hourly" ? hourlyChart.options : daypartsChart.options}
					series={chartMode === "hourly" ? hourlyChart.series : daypartsChart.series}
					type="bar"
					height={320}
				/>
			</SectionCard>

			<SectionCard title={t("order_types")} subtitle={compareLabel}>
				<div className="row g-3">
					{data?.order_type_subs?.map((item, index) => (
						<OrderTypeCard item={item} index={index} key={item.sub_id} />
					))}
					{!data?.order_type_subs?.length && <div className="text-muted">Нет данных</div>}
				</div>
			</SectionCard>

			<div className="row">
				<div className="col-xl-6">
					<SectionCard
						title={t("top_5_dishes")}
						subtitle="Товарная выручка после скидки чека"
						titleIcon={<span className="text-success" style={{ fontSize: "16px" }}>↗</span>}
					>
						<ProductList items={data?.top_products || []} />
					</SectionCard>
				</div>
				<div className="col-xl-6">
					<SectionCard
						title={t("anti_rating_by_revenue")}
						subtitle={applied.compare === "day" ? "Падение к предыдущему дню" : applied.compare === "year" ? "Падение к дню год назад" : "Падение к тому же дню прошлой недели"}
						titleIcon={<span className="text-danger" style={{ fontSize: "16px" }}>↘</span>}
					>
						<ProductList items={data?.anti_rating || []} />
					</SectionCard>
				</div>
			</div>
		</>
	)
}

export default Index
